import { Core } from "@faire/design-tokens";
import { usePOSSummary } from "@faire/retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerPosLoggedInFooterBannerClick as trackLoggedInClick } from "@faire/web-api--source/events/retailerPos/click/loggedInFooterBanner";
import { trackRetailerPosLoggedOutFooterBannerClick as trackLoggedOutClick } from "@faire/web-api--source/events/retailerPos/click/loggedOutFooterBanner";
import { trackRetailerPosLoggedInFooterBannerView as trackLoggedInView } from "@faire/web-api--source/events/retailerPos/view/loggedInFooterBanner";
import { trackRetailerPosLoggedOutFooterBannerView as trackLoggedOutView } from "@faire/web-api--source/events/retailerPos/view/loggedOutFooterBanner";
import { IRetailerLinkCohortEnum } from "@faire/web-api--source/indigofair/retailers/pos/IRetailerLinkCohortEnum";
import posAbout from "@faire/web-api--source/routes/www/point-of-sale/about";
import * as React from "react";
import type { JSX } from "react";
import styled from "styled-components";

export const POSCohortFooterBanner: React.FC = () => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { retailerLinkCohort, name: posCompanyName } = usePOSSummary();
  const bannerInfo = getBannerInfoByCohort(retailerLinkCohort, posCompanyName);

  const actionRef = useDoActionWhenVisible(() => {
    if (isLoggedInRetailer) {
      trackLoggedInView(retailerLinkCohort);
    } else {
      trackLoggedOutView();
    }
  });

  const handleClick = () => {
    if (isLoggedInRetailer) {
      trackLoggedInClick(retailerLinkCohort);
    } else {
      trackLoggedOutClick();
    }
  };

  return bannerInfo ? (
    <Container ref={actionRef}>
      <Typography
        variant="pageHeaderSerifRegular"
        align={{ mobileAndAbove: "left", tabletAndAbove: "right" }}
      >
        {bannerInfo.message}
      </Typography>
      <Spacer height="3x" />
      <Button
        variant="secondary"
        linkTo={posAbout()}
        onClick={handleClick}
        style={{ minWidth: "124px" }}
      >
        {bannerInfo.buttonLabel}
      </Button>
    </Container>
  ) : null;
};

export const getBannerInfoByCohort = (
  retailerLinkCohort: IRetailerLinkCohortEnum.RetailerLinkCohort,
  posCompanyName: string
): IPOSCohortBannerInfo | undefined => {
  switch (retailerLinkCohort) {
    case IRetailerLinkCohortEnum.RetailerLinkCohort.NEW:
      return {
        message: (
          <StrictLocalMsg
            defaultMessage="Faire connects seamlessly with your store's POS system"
            description={{
              text: "Banner message prompting retailers to connect their POS system to Faire",
              img: "https://cdn.faire.com/fastly/adbe38ec3d1dea9a012b90fb75981d06f05570c8cc2ed24d7a40cdfd00087888.png",
            }}
          />
        ),
        buttonLabel: (
          <StrictLocalMsg
            defaultMessage="Learn more"
            description={{
              text: "Learn more CTA text",
              img: "https://cdn.faire.com/fastly/adbe38ec3d1dea9a012b90fb75981d06f05570c8cc2ed24d7a40cdfd00087888.png",
            }}
          />
        ),
      };
    case IRetailerLinkCohortEnum.RetailerLinkCohort.CHURNED:
      return {
        message: (
          <StrictLocalMsg
            defaultMessage="Try our new & improved POS integrations"
            description={{
              text: "Banner message prompting churned retailers to re-connect their POS system to Faire",
              img: "https://cdn.faire.com/fastly/67f22cd34009a260b6f437fc97812c793b51f9e015ea600705939b46b909c896.png",
            }}
          />
        ),
        buttonLabel: (
          <StrictLocalMsg
            defaultMessage="Learn more"
            description={{
              text: "Learn more CTA text",
              img: "https://cdn.faire.com/fastly/adbe38ec3d1dea9a012b90fb75981d06f05570c8cc2ed24d7a40cdfd00087888.png",
            }}
          />
        ),
      };
    case IRetailerLinkCohortEnum.RetailerLinkCohort.INACTIVE:
      return {
        message: (
          <StrictLocalMsg
            defaultMessage="Check out our new & improved {posCompanyName} integration"
            description={{
              text: "Banner message prompting inactive connected retailers to check out the latest POS improvements",
              img: "https://cdn.faire.com/fastly/e46d1782b0ec7b29666211dffc14dc3c9e472d6267537df4859c5e11abfa9789.png",
            }}
            values={{ posCompanyName }}
          />
        ),
        buttonLabel: (
          <StrictLocalMsg
            defaultMessage="View POS integration"
            description={{
              text: "View POS integration CTA text",
              img: "https://cdn.faire.com/fastly/e46d1782b0ec7b29666211dffc14dc3c9e472d6267537df4859c5e11abfa9789.png",
            }}
          />
        ),
      };
    default:
      return undefined;
  }
};

interface IPOSCohortBannerInfo {
  message: JSX.Element;
  buttonLabel: JSX.Element;
}

const Container = styled(Column)`
  padding: ${getSpacing("4x", "3x")};
  border-top: 1px solid ${Core.border.muted};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("4x", 96, "8x")};
    border-top: none;
    align-items: flex-end;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing("3x", 96, "8x", 0)};
    max-width: 400px;
  }
`;
